import React from 'react';

import { tv } from '../../tv';

/**
 * A 2px strip on an edge that says something is being produced behind it:
 * the drawer's top edge while the agent checks a control, the suggestion
 * card's rule while it searches. Indeterminate by nature (there is no
 * progress to report, only presence), and calm: one sweep every 1.6s. The
 * tone's signature paints it, so the AI tone sweeps the gradient and a
 * critical one sweeps red. Under reduced motion the strip simply shows. The
 * bar stays mounted while inactive, paused, so the fade-out has something to fade.
 */
const activityBarStyles = tv({
  slots: {
    root: [
      'ck:relative ck:h-0.5 ck:w-full ck:shrink-0 ck:overflow-hidden ck:pointer-events-none',
      'ck:transition-opacity ck:duration-base',
    ],
    bar: 'ck:absolute ck:inset-y-0 ck:left-0 ck:bg-signature ck:animate-progress',
  },
  variants: {
    active: {
      true: {},
      false: { root: 'ck:opacity-0', bar: 'ck:animation-paused' },
    },
  },
});

export interface ActivityBarProps {
  /**
   * Whether work is happening. The strip keeps its height either way, so
   * the layout underneath never jumps; it only fades.
   */
  isActive?: boolean;
  /**
   * Whose work: `ai` for the agent (the gradient), `critical` for a
   * destructive job in flight. Absent means the brand accent.
   */
  tone?: 'critical' | 'ai';
  /**
   * What is happening, for assistive technology. Without it the strip is
   * decoration beside content that already says so.
   */
  label?: string;
}

export const ActivityBar = React.forwardRef<HTMLDivElement, ActivityBarProps>(function ActivityBar(
  { isActive = true, tone, label },
  ref,
) {
  const { root, bar } = activityBarStyles({ active: isActive });
  return (
    <div
      ref={ref}
      className={root()}
      role={label && isActive ? 'progressbar' : undefined}
      aria-label={label && isActive ? label : undefined}
      aria-busy={label && isActive ? true : undefined}
      aria-hidden={label && isActive ? undefined : true}
      data-component="activity-bar"
      data-tone={tone ?? 'accent'}
    >
      <span className={bar()} />
    </div>
  );
});
